import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Archive, ArrowCounterClockwise, CheckCircle, PaperPlaneTilt, PencilSimple, Trash, X } from "@phosphor-icons/react";
import AccordionCard from "@/components/AccordionCard";
import FilterPills from "@/components/FilterPills";
import { BulkBar, SelectBox, useBulkSelection } from "@/components/BulkSelect";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

function PaymentEditForm({ p, onSave, onCancel }) {
  const [v, setV] = useState({ amount: p.amount, due_date: p.due_date, note: p.note || "", status: p.status });
  return (
    <div className="space-y-2 mt-2" data-testid={`pay-edit-form-${p.payment_id}`}>
      <div className="grid grid-cols-2 gap-2">
        <input type="number" step="0.01" value={v.amount} onChange={(e) => setV({ ...v, amount: e.target.value })}
          data-testid={`pay-edit-amount-${p.payment_id}`} className="px-3 py-2 text-sm" />
        <input type="date" value={v.due_date} onChange={(e) => setV({ ...v, due_date: e.target.value })}
          data-testid={`pay-edit-date-${p.payment_id}`} className="px-3 py-2 text-sm" />
      </div>
      <textarea value={v.note} onChange={(e) => setV({ ...v, note: e.target.value })} rows={2} placeholder="Note"
        data-testid={`pay-edit-note-${p.payment_id}`} className="w-full px-3 py-2 text-sm" />
      <select value={v.status} onChange={(e) => setV({ ...v, status: e.target.value })}
        data-testid={`pay-edit-status-${p.payment_id}`} className="w-full px-3 py-2 text-sm">
        <option value="unpaid">Unpaid</option>
        <option value="paid">Paid</option>
      </select>
      <div className="flex gap-2">
        <button onClick={() => onSave({ ...v, amount: parseFloat(v.amount) })} data-testid={`pay-edit-save-${p.payment_id}`}
          className="btn-primary text-xs flex-1 justify-center">Save</button>
        <button onClick={onCancel} className="btn-ghost text-xs"><X size={12} weight="bold" /></button>
      </div>
    </div>
  );
}

function PaymentItem({ p, userId, onRefresh, sel }) {
  const [editing, setEditing] = useState(false);
  const patch = async (body, msg) => {
    try {
      await api.patch(`/admin/clients/${userId}/payments/${p.payment_id}`, body);
      if (msg) toast.success(msg);
      setEditing(false); onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const del = async () => {
    if (!window.confirm("Delete payment?")) return;
    await api.delete(`/admin/clients/${userId}/payments/${p.payment_id}`);
    onRefresh();
  };
  const remind = async () => {
    try {
      const { data } = await api.post(`/admin/clients/${userId}/reminder-payment/${p.payment_id}`);
      if (data.sent) toast.success("Payment reminder sent"); else toast.warning(data.error || "Failed");
    } catch { toast.error("Failed"); }
  };
  const paid = p.status === "paid";
  return (
    <div className="hair p-4" data-testid={`admin-payment-${p.payment_id}`} style={p.archived ? { opacity: 0.75 } : undefined}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <SelectBox checked={sel.selected.has(p.payment_id)} onChange={(on) => sel.toggle(p.payment_id, on)}
            testid={`select-pay-${p.payment_id}`} />
          <p className="title-xl text-xl">{money(p.amount)}</p>
        </div>
        <div className="flex items-center gap-1">
          {p.archived && <span className="label px-2 py-1 border" style={{ borderColor: "var(--hair)" }}>archived</span>}
          <span className="label px-2 py-1 border"
            style={paid ? { background: "var(--invert-bg)", color: "var(--invert-text)", borderColor: "var(--invert-bg)" }
              : { borderColor: "var(--text)" }}>{p.status}</span>
        </div>
      </div>
      <p className="label mt-2">Due {p.due_date}</p>
      {paid && p.paid_at && <p className="label mt-1" style={{ color: "var(--text-dim)" }}>Paid {new Date(p.paid_at).toLocaleDateString()}</p>}
      {editing ? <PaymentEditForm p={p} onSave={(v) => patch(v, "Payment updated")} onCancel={() => setEditing(false)} /> : (
        <>
          {p.note && <p className="text-sm mt-2 whitespace-pre-line" style={{ color: "var(--text-dim)" }}>{p.note}</p>}
          <div className="flex gap-2 mt-3 flex-wrap">
            {!paid && (
              <button data-testid={`mark-paid-${p.payment_id}`} onClick={() => patch({ status: "paid" }, "Marked paid & archived")}
                className="btn-ghost flex-1 justify-center text-xs"><CheckCircle size={12} weight="bold" /> Paid</button>
            )}
            {!paid && !p.archived && (
              <button data-testid={`send-pay-reminder-${p.payment_id}`} onClick={remind} className="btn-ghost text-xs"
                title="Send payment reminder"><PaperPlaneTilt size={12} weight="bold" /></button>
            )}
            <button data-testid={`edit-pay-${p.payment_id}`} onClick={() => setEditing(true)} className="btn-ghost text-xs"
              title="Edit / add note"><PencilSimple size={12} weight="bold" /></button>
            <button data-testid={`archive-pay-${p.payment_id}`} onClick={() => patch({ archived: !p.archived }, p.archived ? "Restored" : "Archived")}
              className="btn-ghost text-xs" title={p.archived ? "Restore" : "Archive"}>
              {p.archived ? <ArrowCounterClockwise size={12} weight="bold" /> : <Archive size={12} weight="bold" />}
            </button>
            <button data-testid={`del-pay-${p.payment_id}`} onClick={del} className="btn-ghost text-xs" title="Delete">
              <Trash size={12} weight="bold" /></button>
          </div>
        </>
      )}
    </div>
  );
}

function SeriesCard({ sid, items, series, userId, onRefresh, sel }) {
  const itemIds = items.map(p => p.payment_id);
  const paidN = series.filter(p => p.status === "paid").length;
  const total = series.reduce((n, p) => n + Number(p.amount || 0), 0);
  const next = series.filter(p => p.status !== "paid").sort((a, b) => a.due_date.localeCompare(b.due_date))[0];
  const first = [...series].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "") || a.due_date.localeCompare(b.due_date))[0];
  const archiveAll = async (archived) => {
    await api.post(`/admin/clients/${userId}/payments/bulk-archive`, { ids: series.map(p => p.payment_id), archived });
    toast.success(archived ? "Series archived" : "Series restored"); onRefresh();
  };
  const allArchived = series.every(p => p.archived);
  const header = (
    <div>
      <p className="font-semibold">{first.note || "Recurring payments"} · {money(first.amount)} {first.recurring || ""}</p>
      <p className="label mt-1">{paidN} of {series.length} paid · total {money(total)}{next ? ` · next due ${next.due_date}` : ""}</p>
    </div>
  );
  return (
    <AccordionCard testid={`pay-series-${sid}`} header={header}
      actions={<div className="flex items-center gap-2">
        <SelectBox checked={itemIds.every(id => sel.selected.has(id))} indeterminate={itemIds.some(id => sel.selected.has(id))}
          onChange={(on) => sel.toggle(itemIds, on)} testid={`select-series-${sid}`} title="Select whole series" />
        <button onClick={() => archiveAll(!allArchived)} data-testid={`archive-series-${sid}`}
          className="btn-ghost text-xs" title={allArchived ? "Restore series" : "Archive series"}>
          {allArchived ? <ArrowCounterClockwise size={12} weight="bold" /> : <Archive size={12} weight="bold" />}
        </button>
      </div>}>
      <div className="space-y-2 mt-2">
        {items.map(p => <PaymentItem key={p.payment_id} p={p} userId={userId} onRefresh={onRefresh} sel={sel} />)}
      </div>
    </AccordionCard>
  );
}

export default function AdminPaymentsList({ userId, payments, onRefresh }) {
  const [view, setView] = useState("active");
  const sel = useBulkSelection();
  const shown = payments.filter(p => view === "all" || (view === "archived" ? p.archived : !p.archived))
    .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
  const groups = [];
  const bySeries = {};
  shown.forEach(p => {
    if (p.series_id) {
      if (!bySeries[p.series_id]) { bySeries[p.series_id] = []; groups.push({ sid: p.series_id }); }
      bySeries[p.series_id].push(p);
    } else groups.push({ p });
  });
  const count = (v) => payments.filter(p => v === "all" || (v === "archived" ? p.archived : !p.archived)).length;
  return (
    <div className="lg:col-span-2 panel p-6" data-testid="admin-payments-list">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <h4 className="title-xl text-xl">Scheduled</h4>
        <FilterPills testid="pay-view" value={view} onChange={setView}
          options={[{ k: "active", label: `Active (${count("active")})` }, { k: "archived", label: `Archived (${count("archived")})` }, { k: "all", label: "All" }]} />
      </div>
      <div className="mb-3">
        <BulkBar testid="pay-bulk" noun="payments" sel={sel} visibleIds={shown.map(p => p.payment_id)}
          onApply={async (ids, archived) => { await api.post(`/admin/clients/${userId}/payments/bulk-archive`, { ids, archived }); onRefresh(); }} />
      </div>
      {shown.length === 0 && <p className="text-sm" style={{ color: "var(--text-dim)" }}>No {view === "all" ? "" : view} payments</p>}
      <div className="space-y-3">
        {groups.map(g => g.sid
          ? <SeriesCard key={g.sid} sid={g.sid} items={bySeries[g.sid]}
              series={payments.filter(p => p.series_id === g.sid)} userId={userId} onRefresh={onRefresh} sel={sel} />
          : <PaymentItem key={g.p.payment_id} p={g.p} userId={userId} onRefresh={onRefresh} sel={sel} />)}
      </div>
    </div>
  );
}
